import { supabase } from "@/integrations/supabase/client";

// Resize image to max 1028x1028px and ensure file size is under 1MB
export const resizeImageForPrivateAlbum = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.onload = () => {
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      let { width, height } = img;
      
      // Calculate scale to fit within 1028x1028 while maintaining aspect ratio
      const maxSize = 1028;
      const scale = Math.min(maxSize / width, maxSize / height);
      
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw the scaled image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to blob with compression to ensure < 1MB
      const tryCompress = (quality: number) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }
          
          // Check if under 1MB
          if (blob.size <= 1024 * 1024) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else if (quality > 0.3) {
            // Try with lower quality if still too large
            tryCompress(quality - 0.1);
          } else {
            reject(new Error('Could not compress image to under 1MB'));
          }
        }, 'image/jpeg', quality);
      };
      
      tryCompress(0.9);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Validate video file for private albums
export const validateVideoFile = (file: File): Promise<{ duration: number; isValid: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    
    video.onloadedmetadata = () => {
      const duration = video.duration;
      
      // Check file size (max 30MB)
      if (file.size > 30 * 1024 * 1024) {
        resolve({ duration, isValid: false, error: 'Video file size must be less than 30MB' });
        return;
      }
      
      // Check duration (max 15 seconds)
      if (duration > 15) {
        resolve({ duration, isValid: false, error: 'Video duration must be 15 seconds or less. Please trim your video.' });
        return;
      }
      
      resolve({ duration, isValid: true });
    };
    
    video.onerror = () => {
      resolve({ duration: 0, isValid: false, error: 'Invalid video file' });
    };
    
    video.src = URL.createObjectURL(file);
  });
};

export const privateMediaService = {
  async uploadImage(file: File, userId: string, albumId: string): Promise<string> {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Only JPEG, PNG, and WebP images are allowed');
    }
    
    // Resize and compress image
    const processedFile = await resizeImageForPrivateAlbum(file);
    
    // Generate filename
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2);
    const fileName = `${userId}/${albumId}/${timestamp}-${randomSuffix}.jpg`;
    
    // Upload to private-albums bucket
    const { data, error } = await supabase.storage
      .from('private-albums')
      .upload(fileName, processedFile, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      throw new Error(`Upload failed: ${error.message}`);
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('private-albums')
      .getPublicUrl(fileName);

    return publicUrl;
  },

  async uploadVideo(file: File, userId: string, albumId: string): Promise<{ url: string; duration: number }> {
    // Validate video file
    const validation = await validateVideoFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Invalid video file');
    }
    
    // Generate filename
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2);
    const fileExt = file.name.split('.').pop() || 'mp4';
    const fileName = `${userId}/${albumId}/${timestamp}-${randomSuffix}.${fileExt}`;
    
    // Upload to private-albums bucket
    const { data, error } = await supabase.storage
      .from('private-albums')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      throw new Error(`Upload failed: ${error.message}`);
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('private-albums')
      .getPublicUrl(fileName);

    return { url: publicUrl, duration: validation.duration };
  },

  async deleteMedia(mediaUrl: string, userId: string): Promise<void> {
    // Extract filename from URL
    const urlParts = mediaUrl.split('/');
    const fileName = urlParts.slice(-3).join('/'); // userId/albumId/filename
    
    // Security check: ensure user can only delete their own files
    if (!fileName.startsWith(userId + '/')) {
      throw new Error('Unauthorized: You can only delete your own files');
    }
    
    // Delete from storage
    const { error } = await supabase.storage
      .from('private-albums')
      .remove([fileName]);

    if (error) {
      throw new Error(`Delete failed: ${error.message}`);
    }
  }
};